import { AddCommas } from "../../../../config";
import { getAmount } from "../../../../utils/helper";

export const REVENUE_METRICS_STORAGE_KEY = "dash-revenue-metrics-selected";

export const REVENUE_METRIC_OPTIONS = [
  { id: "organic_sales", label: "Organic Sales" },
  { id: "units_sold", label: "Units Sold" },
  { id: "total_sales", label: "Total Sales" },
  { id: "ad_spend", label: "Ad Spend" },
  { id: "tacos", label: "TACoS" },
  { id: "clicks", label: "Clicks" },
  { id: "impressions", label: "Impressions" },
  { id: "ctr", label: "CTR" },
  { id: "aov", label: "AOV" },
  { id: "orders", label: "Orders" },
  { id: "ad_sales", label: "Ad Sales" },
  { id: "refund_rate", label: "Refund Rate" },
  { id: "ad_cvr", label: "Ad Conversion" },
  { id: "ad_attribution", label: "Ad Attribution" },
  { id: "cpc", label: "CPC" },
  { id: "acos", label: "ACoS" },
];

export function getMetricMenuHint(id, revenueKpis) {
  const k = revenueKpis || {};
  switch (id) {
    case "organic_sales":
      return `ROAS ${(k.roas || 0).toFixed(2)}x · ACoS ${(k.acosPct || 0).toFixed(2)}%`;
    case "units_sold":
      return `${AddCommas(Math.round(k.refundUnitsTotal || 0))} refund units`;
    case "total_sales":
      return `${(k.adAttributionPct || 0).toFixed(2)}% ad attribution`;
    case "ad_spend":
      return `ROAS ${(k.roas || 0).toFixed(2)}x`;
    case "tacos":
      return `ACoS ${(k.acosPct || 0).toFixed(2)}%`;
    case "clicks":
      return `${(k.adCvrPct || 0).toFixed(2)}% conversion`;
    case "impressions":
      return `${(k.ctrPct || 0).toFixed(2)}% CTR`;
    case "ctr":
      return `${(k.adCvrPct || 0).toFixed(2)}% CVR`;
    case "aov":
      return `${AddCommas(Math.round(k.ordersTotal || 0))} orders`;
    case "orders":
      return `${AddCommas(Math.round(k.unitsTotalAgg || 0))} units`;
    case "ad_sales":
      return `ROAS ${(k.roas || 0).toFixed(2)}x`;
    case "refund_rate":
      return `${(k.refundRatePct || 0).toFixed(2)}% refund rate`;
    case "ad_cvr":
      return (k.ctrPct || 0) > 0
        ? `CTR ${(k.ctrPct || 0).toFixed(2)}%`
        : `${AddCommas(Math.round(k.clicksTotal || 0))} clicks`;
    case "ad_attribution":
      return `${(k.adAttributionPct || 0).toFixed(1)}% of total`;
    case "cpc":
      return "Avg. cost per click";
    case "acos":
      return `ROAS ${(k.roas || 0).toFixed(2)}x`;
    default:
      return "";
  }
}

export function buildRevenueMetricCardProps(id, revenueKpis, currencySign) {
  const k = revenueKpis || {};
  switch (id) {
    case "organic_sales":
      return {
        accent: "teal",
        title: "Organic Sales",
        value: getAmount(k.organicSalesTotal || 0, currencySign),
        hint: `ROAS ${(k.roas || 0).toFixed(2)}x · ACoS ${(k.acosPct || 0).toFixed(2)}%`,
        trend: k.organicSalesTrend,
        deltaMode: "currency",
        currencySign,
      };
    case "units_sold":
      return {
        accent: "slate",
        title: "Units Sold",
        value: AddCommas(Math.round(k.unitsTotalAgg || 0)),
        hint: `${AddCommas(Math.round(k.refundUnitsTotal || 0))} refund units`,
        trend: k.unitsSoldTrend,
        deltaMode: "count",
      };
    case "total_sales":
      return {
        accent: "blue",
        title: "Total Sales",
        value: getAmount(k.totalSalesTotal || 0, currencySign),
        hint: `${(k.adAttributionPct || 0).toFixed(2)}% ad attribution`,
        trend: k.totalSalesTrend,
        deltaMode: "currency",
        currencySign,
      };
    case "ad_spend":
      return {
        accent: "rose",
        title: "Ad Spend",
        value: getAmount(k.adSpendTotal || 0, currencySign),
        hint: `ROAS ${(k.roas || 0).toFixed(2)}x`,
        trend: k.adSpendTrend,
        deltaMode: "currency",
        currencySign,
        invertTrendColors: true,
      };
    case "tacos":
      return {
        accent: "indigo",
        title: "TACoS",
        value: `${(k.tacosPct || 0).toFixed(2)}%`,
        hint: `ACoS ${(k.acosPct || 0).toFixed(2)}%`,
        trend: k.tacosTrend,
        deltaMode: "rate",
        invertTrendColors: true,
      };
    case "clicks":
      return {
        accent: "violet",
        title: "Clicks",
        value: AddCommas(Math.round(k.clicksTotal || 0)),
        hint: `${(k.adCvrPct || 0).toFixed(2)}% conversion`,
        trend: k.clicksTrend,
        deltaMode: "count",
      };
    case "impressions":
      return {
        accent: "amber",
        title: "Impressions",
        value: AddCommas(Math.round(k.impressionsTotal || 0)),
        hint: `${(k.ctrPct || 0).toFixed(2)}% CTR`,
        trend: k.impressionsTrend,
        deltaMode: "count",
      };
    case "ctr":
      return {
        accent: "coral",
        title: "CTR",
        value: `${(k.ctrPct || 0).toFixed(2)}%`,
        hint: `${(k.adCvrPct || 0).toFixed(2)}% CVR`,
        trend: k.ctrTrend,
        deltaMode: "rate",
      };
    case "aov":
      return {
        accent: "slate",
        title: "AOV",
        value: getAmount(k.aovValue || 0, currencySign),
        hint: `${AddCommas(Math.round(k.ordersTotal || 0))} orders`,
        trend: k.aovTrend,
        deltaMode: "currency",
        currencySign,
      };
    case "orders":
      return {
        accent: "slate",
        title: "Orders",
        value: AddCommas(Math.round(k.ordersTotal || 0)),
        hint: `${AddCommas(Math.round(k.unitsTotalAgg || 0))} units`,
        trend: k.ordersTrend,
        deltaMode: "count",
      };
    case "ad_sales":
      return {
        accent: "blue",
        title: "Ad Sales",
        value: getAmount(k.adSalesTotal || 0, currencySign),
        hint: `ROAS ${(k.roas || 0).toFixed(2)}x`,
        trend: k.adSalesTrend,
        deltaMode: "currency",
        currencySign,
      };
    case "refund_rate":
      return {
        accent: "rose",
        title: "Refund Rate",
        value: `${(k.refundRatePct || 0).toFixed(2)}%`,
        hint: `${AddCommas(Math.round(k.refundUnitsTotal || 0))} units`,
        trend: k.refundTrend,
        deltaMode: "percent",
        invertTrendColors: true,
      };
    case "ad_cvr":
      return {
        accent: "teal",
        title: "Ad Conversion",
        value: `${(k.adCvrPct || 0).toFixed(2)}%`,
        hint: `${AddCommas(Math.round(k.clicksTotal || 0))} clicks`,
        trend: k.adCvrTrend,
        deltaMode: "percent",
      };
    case "ad_attribution":
      return {
        accent: "violet",
        title: "Ad Attribution",
        value: `${(k.adAttributionPct || 0).toFixed(2)}%`,
        hint: "Ad sales share of revenue",
        trend: k.adAttributionTrend,
        deltaMode: "rate",
      };
    case "cpc":
      return {
        accent: "amber",
        title: "CPC",
        value: getAmount(k.cpcValue || 0, currencySign),
        hint: (k.ctrPct || 0) > 0 ? `${(k.ctrPct || 0).toFixed(2)}% CTR` : "Cost per click",
        trend: k.cpcTrend,
        deltaMode: "currency",
        currencySign,
        invertTrendColors: true,
      };
    case "acos":
      return {
        accent: "coral",
        title: "ACoS",
        value: `${(k.acosPct || 0).toFixed(2)}%`,
        hint: `ROAS ${(k.roas || 0).toFixed(2)}x`,
        trend: k.acosTrend,
        deltaMode: "rate",
        invertTrendColors: true,
      };
    default:
      return null;
  }
}

export function loadSelectedRevenueMetrics() {
  try {
    const raw = localStorage.getItem(REVENUE_METRICS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const valid = new Set(REVENUE_METRIC_OPTIONS.map((o) => o.id));
    return parsed.filter((id) => valid.has(id));
  } catch {
    return [];
  }
}

export function saveSelectedRevenueMetrics(ids) {
  try {
    localStorage.setItem(REVENUE_METRICS_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}
